import { createContext, useContext, useReducer, useMemo, type ReactNode, type Dispatch } from "react";
import type { ViewName } from "@/types";

// ─── State ───
export interface NavigationState {
  view: ViewName;
  category: string | null;
  subcategory: string | null;
  preferencesOpen?: boolean;
  calendarsOpen?: boolean;
  showCalendarOverlay?: boolean;
}

export const navInitialState: NavigationState = {
  view: "landing",
  category: null,
  subcategory: null,
  preferencesOpen: false,
  calendarsOpen: false,
  showCalendarOverlay: false,
};

// ─── Actions ───
export type NavigationAction =
  | { type: "SET_VIEW"; view: ViewName }
  | { type: "SELECT_CATEGORY"; category: string | null }
  | { type: "SELECT_SUBCATEGORY"; subcategory: string | null }
  | { type: "NAV_RESET" };

export function navigationReducer(state: NavigationState, action: NavigationAction): NavigationState {
  switch (action.type) {
    case "SET_VIEW":
      return { ...state, view: action.view };
    case "SELECT_CATEGORY":
      return { ...state, category: action.category, subcategory: null };
    case "SELECT_SUBCATEGORY":
      return { ...state, subcategory: action.subcategory };
    case "NAV_RESET":
      return { ...navInitialState };
    default:
      return state;
  }
}

// ─── Context ───
interface NavigationContextValue {
  nav: NavigationState;
  navDispatch: Dispatch<NavigationAction>;
}

const NavigationContext = createContext<NavigationContextValue | null>(null);

export function NavigationProvider({ children }: { children: ReactNode }) {
  const [nav, navDispatch] = useReducer(navigationReducer, navInitialState);
  const value = useMemo(() => ({ nav, navDispatch }), [nav]);
  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

export function useNavigation() {
  const ctx = useContext(NavigationContext);
  if (!ctx) throw new Error("useNavigation must be used within NavigationProvider");
  return ctx;
}
